import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionableNotification, Button, InlineLoading } from '@carbon/react';
import { View, ViewOff } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useStore } from '@tanstack/react-store';
import classnames from 'classnames';

import { useWizardStep } from 'components/full-page-wizard';
import { TypeCurveScenarioPicker } from 'components/type-curve-scenario-picker/type-curve-scenario-picker';

import { AddLtdModal } from '../add-ltd-modal';
import { AddWellboreModal } from '../add-wellbore-modal';
import {
  awaitingDraftMaterializationAtom,
  currentDraftSummaryAtom,
  draftAssetChangePendingAtom,
  draftMapFeaturesAtom,
  draftRebuildFailureAtom,
  draftRebuildsEndedAtom,
  draftReloadAtom,
  draftSelectedAssetCountAtom,
} from '../atoms';
import { contributingFileName } from '../asset-list-utils';
import { usePackageWizardCtx } from '../context';
import { useDraftRebuildTryAgain } from '../draft-build-hooks';
import { useAssetListSummary } from '../hooks';
import {
  useCloneNotices,
  useCloneNoticesShown,
} from '../ownership-changes-hooks';
import { reviewTabFor } from '../ownership-changes-utils';
import { PackageWizardMap } from '../package-wizard-map';
import type { DraftRebuildFailure, SourceType } from '../types';
import { entityOptionsFromSources } from '../utils';
import { AssetsSlotsSplitPane } from './assets-slots-split-pane';
import { AssetsTable } from './assets-table';
import { CloneNoticeBanners } from './clone-notice-banners';
import { OwnershipChangesModal } from './ownership-changes-modal';
import { SlotsTable } from './slots-table';
import { Step1SummaryPanel } from './step1-summary-panel';
import styles from './select-assets-slots-step.module.scss';

/** The sources that carry the asset list: as the source, or beside it. */
const LIST_SOURCES = new Set<SourceType | null>([
  'assetList',
  'mapQuery',
  'package',
]);

/** Props for {@link RebuildFailureNotice}. */
interface RebuildFailureNoticeProps {
  /** How the step's last wait for a build ended, when not ready. */
  failure: DraftRebuildFailure | null;
  /** A scenario switch has failed since, and offers its own Retry. */
  scenarioFailed: boolean;
  onTryAgain: (failure: DraftRebuildFailure) => void;
}

/**
 * Says how the step's last wait for a build ended when not ready, with Try
 * again. One notice per failed draft: once a scenario switch has failed too,
 * only its Retry shows, which rebuilds the whole draft on that scenario. A wait
 * that ran out keeps its Try again: it waits on a build perhaps still running.
 *
 * @param {RebuildFailureNoticeProps} props - The failure, whether a switch failed since, and Try again
 * @return {JSX.Element | null} The notice, or nothing
 */
function RebuildFailureNotice({
  failure,
  scenarioFailed,
  onTryAgain,
}: RebuildFailureNoticeProps) {
  if (failure === null || (scenarioFailed && !failure.timedOut)) return null;
  return (
    <ActionableNotification
      inline
      role="alert"
      closeOnEscape={false}
      kind="error"
      hideCloseButton
      lowContrast
      className={styles.scenarioError}
      title={
        failure.timedOut
          ? 'Rebuilding properties timed out'
          : 'Rebuilding properties failed'
      }
      subtitle={failure.message}
      actionButtonLabel="Try again"
      onActionButtonClick={() => onTryAgain(failure)}
    />
  );
}

/**
 * Step 1 — Select Assets & Properties. Three-column layout (tables / map /
 * summary) with per-column show/hide toggles. Both tables are BE-backed:
 * `AssetsTable` drives asset membership via
 * `PATCH /packages/drafts/:id/assets`; `SlotsTable` drives slot selection
 * via `PATCH /packages/drafts/:id/slot_selections`. Neither selection set
 * lives on the form — the draft is the server-side source of truth and
 * `POST /packages` (Step 2) reads from it directly.
 *
 * Selection state is owned by `AssetsTable` (and by the draft-slots
 * response for `SlotsTable`); this step no longer tracks it. The
 * `AssetsTable` remounts via `key={draftId}` whenever a new draft is
 * created so its local selection resets cleanly.
 *
 * Layout: CSS Grid with 4 permutations driven by `showMap` + `showSummary`.
 * Tables column absorbs the remainder; map and summary are fixed-width per
 * the v5 mock (`400px` / `364px`), with the map growing to `764px` when
 * summary is hidden to preserve the visual balance.
 *
 * A change that queues a build (a Subtract change, a Tract held in part
 * unticked or made whole) holds the whole step until the build is done, as a
 * scenario switch does: one "Rebuilding properties…" beside the picker, the
 * picker, both tables, Back and Next held. A wait that times out keeps the
 * tables held, the build perhaps still running, and offers Try again, which
 * waits on the same draft again; a failed build keeps Next shut and offers Try
 * again, which has the server build the draft again and waits for that.
 *
 * A clone shows its notices above the tables, read from the draft on arrival
 * and once each build or scenario switch has ended (a tick cannot change
 * them): the exclusions the sources carried over, and the assets the As of
 * Date removed and added, with the review that adds them ("Hide
 * Notifications" hides both).
 *
 * Next gates on at least one selected asset, derived from
 * `draftSelectedAssetCountAtom`. That atom rolls up the same summary
 * `AssetsTable` / `SlotsTable` write on every successful query and
 * PATCH, so the gate stays in sync with the BE without a separate
 * count fetch. Pre-first-fetch the count is 0 → Next stays disabled.
 *
 * @return {JSX.Element} Step 1 content
 */
export const SelectAssetsSlotsStep: FC = () => {
  const { form, api } = usePackageWizardCtx();
  const { setNextEnabled, setBackEnabled } = useWizardStep();

  const sourceType = useStore(form.store, (s) => s.values.sourceType);
  const sources = useStore(form.store, (s) => s.values.sources);
  const draftId = useStore(form.store, (s) => s.values.draftId);
  const scenarioId = useStore(form.store, (s) => s.values.typeCurveScenarioId);
  const asOfDate = useStore(form.store, (s) => s.values.asOfDate);
  const mapQueryFilters = useStore(form.store, (s) => s.values.mapQuery);
  const assetListFile = useStore(form.store, (s) => s.values.assetList.file);
  const assetList = useAssetListSummary(form);
  const assetListFileName = useMemo(
    () => contributingFileName(assetListFile, assetList.matched),
    [assetListFile, assetList.matched]
  );
  const hasSelectedAsset = useAtomValue(draftSelectedAssetCountAtom) > 0;
  // An asset change waiting to be sent (Subtract ticks in their pause), in
  // flight, or building holds Next, Back and the scenario switch: Summary reads
  // the draft and Create refuses one still building, and neither Back nor a
  // switch may leave or rebuild the draft under it.
  const assetChangePending = useAtomValue(draftAssetChangePendingAtom);
  const fetchMapFeatures = useSetAtom(draftMapFeaturesAtom);
  const resetDraftSummary = useSetAtom(currentDraftSummaryAtom);
  // The wait for a build an asset change queued, and how the last one ended
  // when not ready.
  const awaitingBuild = useAtomValue(awaitingDraftMaterializationAtom);
  const [rebuildFailure, setRebuildFailure] = useAtom(draftRebuildFailureAtom);
  const tryAgain = useDraftRebuildTryAgain(draftId);
  const [reload, setReload] = useAtom(draftReloadAtom);
  const [rebuildsEnded, setRebuildsEnded] = useAtom(draftRebuildsEndedAtom);
  // A clone's notices; the review opens from the ownership one.
  const notices = useCloneNotices(draftId, sourceType === 'package');
  const ownershipChanges = notices.data?.ownershipChanges ?? null;
  const noticesShown = useCloneNoticesShown(notices.data);
  const [reviewOpen, setReviewOpen] = useState(false);
  const sourceNames = useMemo(() => sources.map(({ name }) => name), [sources]);
  // The list is the source on Asset List, and "Uploaded Tracts" beside Map
  // Query or Existing Package; no other source takes one.
  const summaryAssetList = LIST_SOURCES.has(sourceType)
    ? {
        matched: assetList.matched.length,
        fileName: assetListFileName,
        entities: assetList.entities,
      }
    : undefined;

  const [showMap, setShowMap] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [addWellboreOpen, setAddWellboreOpen] = useState(false);
  const [addLtdOpen, setAddLtdOpen] = useState(false);
  // True while a scenario switch re-materializes. The picker and both tables are
  // inert until it settles — the LTD rows are being rebuilt underneath them.
  const [switchingScenario, setSwitchingScenario] = useState(false);
  // Either rebuild holds the whole step.
  const waiting = switchingScenario || awaitingBuild;
  // A wait that ran out holds the tables too: the build may still be running.
  const rebuilding = waiting || rebuildFailure?.timedOut === true;
  // A switch that failed, and the scenario it was switching to. Retry re-sends that
  // scenario: if the PATCH failed the draft is unchanged, and if the rebuild failed the BE
  // rebuilds a failed draft even on the same scenario.
  const [scenarioError, setScenarioError] = useState<{
    message: string;
    scenarioId: number;
  } | null>(null);

  // Repointing the draft at another scenario rebuilds its LTD population from that
  // scenario's published inventory, and selections deliberately do NOT carry across
  // (product ruling): every rebuilt row comes back selected. Both tables and the map
  // therefore have to refetch once the job reports ready. `api.setScenario` keeps the
  // form's scenario in step with the draft's, including when the rebuild fails.
  const handleScenarioChange = useCallback(
    async (nextScenarioId: number) => {
      setSwitchingScenario(true);
      setScenarioError(null);
      try {
        await api.setScenario(nextScenarioId);
        // The switch waited for the draft to be ready: an earlier build's failure is over.
        setRebuildFailure(null);
        setReload((count) => count + 1);
        setRebuildsEnded((count) => count + 1);
      } catch (error) {
        setScenarioError({
          message: error instanceof Error ? error.message : String(error),
          scenarioId: nextScenarioId,
        });
      } finally {
        setSwitchingScenario(false);
      }
    },
    [api, setRebuildFailure, setReload, setRebuildsEnded]
  );

  // Which sellers a new wellbore / LTD asset may be filed under. Derived from
  // the Step 0 selection rather than fetched, so it tracks a source change
  // with no round trip. On the asset list: the matched Tracts' owners.
  const entityOptions = useMemo(
    () =>
      entityOptionsFromSources({
        sourceType,
        sources,
        assetListEntities: assetList.entities,
      }),
    [sourceType, sources, assetList.entities]
  );

  const handleOpenWellbore = useCallback(() => setAddWellboreOpen(true), []);
  const handleOpenLtd = useCallback(() => setAddLtdOpen(true), []);

  const handleAssetAdded = useCallback(
    () => setReload((count) => count + 1),
    [setReload]
  );

  // Clear stale summary from any previous draft so the Next-button gate
  // starts disabled until the fresh asset query for this draft returns. A
  // wait that did not end ready on an earlier visit is over: Next on Select
  // Source waited for the draft again on the way back.
  useEffect(() => {
    resetDraftSummary(null);
    setRebuildFailure(null);
  }, [resetDraftSummary, setRebuildFailure]);

  // A scenario is mandatory. Next stays shut while a switch is in flight or after one failed,
  // so the user cannot advance on properties that don't match the draft's scenario; Back stays
  // shut mid-switch for the same reason. Back stays shut while Subtract ticks wait to be sent, so
  // the batch goes out with the step still open rather than after it, from the unmount flush;
  // Next stays shut until any asset change is sent and built, and after a build that did not
  // end ready (Create refuses a draft that is not ready). An asset change holds Back and the
  // picker from the moment it is sent, before the server says whether it queued a build, so a
  // build it queues is always awaited with the step still open.
  useEffect(() => {
    setNextEnabled(
      hasSelectedAsset &&
        scenarioId !== null &&
        !switchingScenario &&
        scenarioError === null &&
        !assetChangePending &&
        rebuildFailure === null
    );
  }, [
    hasSelectedAsset,
    scenarioId,
    switchingScenario,
    scenarioError,
    assetChangePending,
    rebuildFailure,
    setNextEnabled,
  ]);

  useEffect(() => {
    setBackEnabled(!switchingScenario && !assetChangePending);
  }, [switchingScenario, assetChangePending, setBackEnabled]);

  // Owns the features fetch trigger, on mount and after each settled change
  // to the draft (`draftReloadAtom`). The map is a pure consumer — if
  // the trigger lived inside the map, it would re-fire every time the map
  // remounts on a layout change.
  useEffect(() => {
    if (draftId !== null) fetchMapFeatures(draftId);
  }, [draftId, reload, fetchMapFeatures]);
  // Leaving drops them, and any read in flight: Back to Select Source then Next
  // can rebuild the draft, and the map must not open on, or fit to, what this
  // visit read.
  useEffect(
    () => () => {
      void fetchMapFeatures(null);
    },
    [fetchMapFeatures]
  );

  // Mapbox-gl sizes its canvas on mount from the container's width and
  // doesn't observe parent size changes. A plain `resize` dispatch isn't
  // enough here — the canvas gets stuck. Remounting the component via a
  // layout-derived `key` forces a fresh mapbox instance that reads the
  // current container size at mount time.
  const mapKey = `${showMap ? 'map' : 'no-map'}-${
    showSummary ? 'sum' : 'no-sum'
  }`;

  const layoutClass = classnames(styles.layout, {
    [styles.layoutAll]: showMap && showSummary,
    [styles.layoutNoMap]: !showMap && showSummary,
    [styles.layoutNoSummary]: showMap && !showSummary,
    [styles.layoutNeither]: !showMap && !showSummary,
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TypeCurveScenarioPicker
          value={scenarioId}
          onChange={handleScenarioChange}
          disabled={switchingScenario || draftId === null || assetChangePending}
        />
        {/*
          Switching scenario re-materializes the draft's LTD population on the BE, and
          `api.setScenario` awaits that job, so the wait is as long as a materialization —
          seconds, not milliseconds. Until this, the only feedback was the picker going
          disabled: the tables kept rendering the OLD rows and the step read as frozen.
          Sits beside the picker because that is the control the user just used. A build an
          asset change queued shows here too: the step's one wait.
        */}
        {waiting && (
          <InlineLoading
            className={styles.scenarioLoading}
            status="active"
            description="Rebuilding properties…"
          />
        )}
        {noticesShown.toggleable && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={noticesShown.shown ? ViewOff : View}
            onClick={noticesShown.toggle}
          >
            {noticesShown.shown ? 'Hide Notifications' : 'Show Notifications'}
          </Button>
        )}
        <Button
          kind="ghost"
          size="sm"
          renderIcon={showMap ? ViewOff : View}
          onClick={() => setShowMap((prev) => !prev)}
        >
          {showMap ? 'Hide Map' : 'Show Map'}
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={showSummary ? ViewOff : View}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          {showSummary ? 'Hide Summary' : 'Show Summary'}
        </Button>
      </div>
      {/*
        role="alert", as the wizard's export notice has it: an alertdialog takes focus on every
        render, and these stay up while the tables are in use. No Escape: there is no close.
      */}
      {scenarioError !== null && (
        <ActionableNotification
          inline
          role="alert"
          closeOnEscape={false}
          kind="error"
          hideCloseButton
          lowContrast
          className={styles.scenarioError}
          title="Rebuilding properties for this scenario failed"
          subtitle={scenarioError.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() =>
            handleScenarioChange(scenarioError.scenarioId)
          }
        />
      )}
      <RebuildFailureNotice
        failure={rebuildFailure}
        scenarioFailed={scenarioError !== null}
        onTryAgain={tryAgain}
      />
      <div className={layoutClass}>
        <div className={styles.tablesCol}>
          <CloneNoticeBanners
            notices={notices.data}
            error={notices.error}
            sourceNames={sourceNames}
            showExclusions={noticesShown.exclusions}
            onExclusionsClose={noticesShown.closeExclusions}
            showOwnership={noticesShown.ownership}
            onOwnershipClose={noticesShown.closeOwnership}
            onReview={() => setReviewOpen(true)}
          />
          <div className={styles.tablesPane}>
            <AssetsSlotsSplitPane
              top={
                draftId !== null ? (
                  <AssetsTable
                    key={draftId}
                    draftId={draftId}
                    allEntities={sourceType === 'assetList'}
                    onAddWellbore={handleOpenWellbore}
                    onAddLtd={handleOpenLtd}
                    disableSelection={rebuilding}
                  />
                ) : null
              }
              bottom={
                draftId !== null ? (
                  // Re-keyed on the scenario so a switch resets filters, sort and
                  // paging: the rows underneath are a different population, and
                  // carrying a filter over from the previous one would silently
                  // hide slots the user has never seen.
                  <SlotsTable
                    key={`${draftId}-${scenarioId ?? 'none'}`}
                    draftId={draftId}
                    disableSelection={rebuilding}
                    disableRemove={rebuilding}
                  />
                ) : null
              }
            />
          </div>
        </div>
        {showMap && (
          <div
            className={classnames(styles.mapCol, {
              [styles.mapColSolo]: !showSummary,
            })}
          >
            <PackageWizardMap
              key={mapKey}
              draftId={draftId}
              refitKey={rebuildsEnded}
            />
          </div>
        )}
        {showSummary && (
          <div className={styles.summaryCol}>
            <Step1SummaryPanel
              sourceType={sourceType}
              sources={sources}
              assetList={summaryAssetList}
              mapQuery={{ locations: mapQueryFilters.locations }}
            />
          </div>
        )}
      </div>
      {draftId !== null && (
        <>
          {/*
            "From Existing Package" sources offer the source packages' seller
            entities (captured on each row in select-source-step.tsx) — a
            SUBSET of what the BE `Packages::Creator` inherits at final submit,
            not a mirror of it; see `entityOptionsFromSources`. The list is
            empty when any selected row's sellers are unknown, and each modal
            then explains why it can't file an asset.
          */}
          <AddWellboreModal
            open={addWellboreOpen}
            onClose={() => setAddWellboreOpen(false)}
            entityOptions={entityOptions}
            draftId={draftId}
            onSuccess={handleAssetAdded}
          />
          <AddLtdModal
            open={addLtdOpen}
            onClose={() => setAddLtdOpen(false)}
            entityOptions={entityOptions}
            draftId={draftId}
            onSuccess={handleAssetAdded}
          />
          {reviewOpen && (
            <OwnershipChangesModal
              draftId={draftId}
              asOfDate={asOfDate}
              initialTab={reviewTabFor(ownershipChanges)}
              held={rebuilding}
              onClose={() => setReviewOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};
